/**
 * The game moved from vibekommandant.com to play.vibekommandant.com, and browser storage is
 * per origin. On the first boot at the new host, a hidden frame loads a page from the old
 * origin (`/migrate-storage`, the landing site's `public/migrate-storage.html` in
 * joonsp/vibekommandant-site) that posts its settings and single-player save back; we copy any key this origin does not
 * have yet. Runs before the game's modules load (`src/boot.ts`), so the stores read the result.
 *
 * Both hosts are one site, so the frame gets the old origin's real (unpartitioned) storage.
 */

export const OLD_ORIGIN = 'https://vibekommandant.com';
export const NEW_HOST = 'play.vibekommandant.com';
export const MIGRATION_PAGE = `${OLD_ORIGIN}/migrate-storage`;
export const MIGRATED_FLAG = 'vibekommandant_origin_migrated';
/** What carries over: settings (incl. campaign progress) and the skirmish autosave. */
export const MIGRATED_KEYS = ['vibekommandant_settings', 'vibekommandant_sp_state'] as const;

interface MigrationWindow {
  location: { hostname: string };
  localStorage: Storage;
  document: Document;
  addEventListener(type: 'message', fn: (e: MessageEvent) => void): void;
  removeEventListener(type: 'message', fn: (e: MessageEvent) => void): void;
  setTimeout(fn: () => void, ms: number): unknown;
}

/** Copies the old origin's keys this origin lacks. Pure, for tests. */
export function applyMigratedStorage(storage: Storage, data: unknown): number {
  if (!data || typeof data !== 'object') return 0;
  let copied = 0;
  for (const key of MIGRATED_KEYS) {
    const value = (data as Record<string, unknown>)[key];
    if (typeof value === 'string' && storage.getItem(key) === null) {
      storage.setItem(key, value);
      copied++;
    }
  }
  return copied;
}

/**
 * Resolves once migration is done, skipped or timed out — never rejects, never blocks the
 * game for longer than `timeoutMs`. Marked done only when the old origin answered, so a
 * timeout retries on the next boot (copying only what is still missing).
 */
export function migrateFromOldOrigin(
  win: MigrationWindow = window as unknown as MigrationWindow,
  timeoutMs = 2500
): Promise<void> {
  try {
    if (win.location.hostname !== NEW_HOST) return Promise.resolve();
    if (win.localStorage.getItem(MIGRATED_FLAG)) return Promise.resolve();
  } catch {
    return Promise.resolve();
  }

  return new Promise(resolve => {
    const frame = win.document.createElement('iframe');
    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      win.removeEventListener('message', onMessage);
      frame.remove();
      resolve();
    };
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== OLD_ORIGIN || e.data?.type !== 'vk-storage') return;
      try {
        applyMigratedStorage(win.localStorage, e.data.data);
        win.localStorage.setItem(MIGRATED_FLAG, new Date().toISOString());
      } catch {
        // Storage full or blocked: start fresh rather than fail to boot.
      }
      finish();
    };
    win.addEventListener('message', onMessage);
    frame.style.display = 'none';
    frame.src = MIGRATION_PAGE;
    win.document.body.appendChild(frame);
    win.setTimeout(finish, timeoutMs);
  });
}
